<script setup lang="ts">
import { computed, ref } from 'vue'
import { RouterView, useRoute } from 'vue-router'
import { useLogout } from '@modules/auth'
import { useSellerProfileQuery } from '@modules/profile'
import { NotificationsDrawer } from '@modules/notifications'
import Box from '@uiKit/elements/Box.vue'
import Drawer from '@uiKit/elements/Drawer.vue'
import AppSidebar from './AppSidebar.vue'
import AppShellHeader from './AppShellHeader.vue'

// Authenticated app shell: fixed left sidebar + main column (header + scrollable
// content). The header's title/subtitle come from the active route's meta.
// On mobile the sidebar collapses into a left drawer.
//
// Scaffold seam — prime app-shell queries (KYC, notifications) here on mount
// when those modules land. Logout runs the full auth flow (server logout →
// local clear → UAE Pass single-logout redirect) via the auth module.
const route = useRoute()
const mobileNavOpen = ref(false)
const notificationsOpen = ref(false)
const { logout: handleLogout } = useLogout()

// Primed here so every downstream consumer (dashboard greeting, listing-request
// containers) reads the same cached profile instead of refetching per screen.
const profileQuery = useSellerProfileQuery()

// Routes flagged `greetUser` swap their static title for a personalised
// greeting; `meta.title` stays the fallback until the profile resolves.
const headerTitle = computed(() => {
  const fallback = String(route.meta.title ?? 'Dashboard')
  if (!route.meta.greetUser) return fallback
  const fullName = profileQuery.data.value?.fullName
  return fullName ? `Welcome, ${fullName}` : fallback
})
const headerSubtitle = computed(() => String(route.meta.subtitle ?? ''))
</script>

<template>
  <Box flex class="app-shell">
    <Box class="app-shell__rail">
      <AppSidebar />
    </Box>

    <Box direction="col" grow class="app-shell__main">
      <AppShellHeader
        :title="headerTitle"
        :subtitle="headerSubtitle"
        @toggle-nav="mobileNavOpen = true"
        @open-notifications="notificationsOpen = true"
        @logout="handleLogout"
      />

      <Box is="main" class="app-shell__content">
        <Box
          class="app-shell__page"
          :class="{ 'app-shell__page--full-height': route.meta.fullHeight }"
        >
          <RouterView />
        </Box>
      </Box>
    </Box>

    <Drawer
      v-model="mobileNavOpen"
      position="left"
      width="260px"
      :show-close-icon="false"
      class="app-shell__drawer"
    >
      <AppSidebar @navigate="mobileNavOpen = false" />
    </Drawer>

    <NotificationsDrawer v-model="notificationsOpen" />
  </Box>
</template>

<style scoped>
.app-shell {
  height: 100dvh;
  overflow: hidden;
  background: var(--p-surface-50);
}

.app-shell__main {
  min-width: 0;
}

/* Positioned so it is the containing block for any absolutely positioned
   element in a page: those then scroll and clip inside it, rather than
   anchoring to the viewport and making the whole document scroll. */
.app-shell__content {
  position: relative;
  flex: 1;
  overflow-y: auto;
  scrollbar-width: none;
  min-height: 0;
  display: flex;
  flex-direction: column;
}

.app-shell__content::-webkit-scrollbar {
  display: none;
}

.app-shell__page {
  flex: 1 0 auto;
  display: flex;
  flex-direction: column;
  min-height: 0;
}

.app-shell__page--full-height {
  flex: 1 1 0%;
  overflow: hidden;
}

/* Mobile: hide the fixed rail, sidebar lives in the drawer instead. */
@media (max-width: 991px) {
  .app-shell__rail {
    display: none;
  }
}

@media (min-width: 992px) {
  .app-shell__drawer {
    display: none;
  }
}
</style>
